import { Component, Input, OnChanges } from '@angular/core';
import { NAICS_COLORS } from '../../Shared/threat-colors';

type NaicsEntry = { naics_code: string; naics_title: string; count: number };
type NaicsDict = Record<string, NaicsEntry[]>;

interface SectorEntry {
    code: string;
    friendly: string;
    count: number;
}

interface IndustryListItem {
    rank: number;
    code: string;
    title: string;
    count: number;
    sectorCode: string;
    sectorFriendly: string;
}

@Component({
    selector: 'app-industry-sector-drilldown',
    templateUrl: './industry-sector-drilldown.component.html',
    styleUrls: ['./industry-sector-drilldown.component.css'],
    standalone: false
})
export class IndustrySectorDrilldownComponent implements OnChanges {
    @Input() naics_dict: NaicsDict = {};
    @Input() isDarkTheme = false;

    allSectors: SectorEntry[] = [];
    selectedSectorIndustries: IndustryListItem[] = [];
    selectedSectorCode = '';
    selectedSectorLabel = '';

    selectedAggLevel: 3 | 4 | 5 | 6 = 6;

    readonly digitLevels: { agglevel: 3 | 4 | 5 | 6; friendly: string }[] = [
        { agglevel: 3, friendly: '3 Digit Codes: Subsectors' },
        { agglevel: 4, friendly: '4 Digit Codes: Industry Groups' },
        { agglevel: 5, friendly: '5 Digit Codes: NAICS Industries' },
        { agglevel: 6, friendly: '6 Digit Codes: National Industries' },
    ];

    ngOnChanges(): void {
        this.buildSectorData();
    }

    buildSectorData(): void {
        const naicsTwoDigit = (this.naics_dict['2'] ?? []).filter(i => i.naics_code !== '0');

        this.allSectors = naicsTwoDigit
            .filter(s => s.count > 0)
            .sort((a, b) => b.count - a.count)
            .map(s => ({ code: s.naics_code, friendly: s.naics_title, count: s.count }));

        const current = this.allSectors.find(s => s.code === this.selectedSectorCode) ?? null;
        this.selectSector(current);
    }

    onSectorChange(event: Event): void {
        const code = (event.target as HTMLSelectElement).value;
        this.selectSector(code ? (this.allSectors.find(s => s.code === code) ?? null) : null);
    }

    onLevelChange(event: Event): void {
        this.selectedAggLevel = +((event.target as HTMLSelectElement).value) as 3 | 4 | 5 | 6;
        const current = this.selectedSectorCode ? (this.allSectors.find(s => s.code === this.selectedSectorCode) ?? null) : null;
        this.selectSector(current);
    }

    selectSector(sector: SectorEntry | null): void {
        this.selectedSectorCode = sector?.code ?? '';
        this.selectedSectorLabel = sector?.friendly ?? 'All Sectors';

        const entries = (this.naics_dict[String(this.selectedAggLevel)] ?? [])
            .filter(i => i.naics_code !== '0' && (sector ? i.naics_code.startsWith(sector.code) : true))
            .sort((a, b) => b.count - a.count);

        this.selectedSectorIndustries = entries.slice(0, 10).map((item, i) => {
            const sectorCode = item.naics_code.substring(0, 2);
            const sectorEntry = this.allSectors.find(s => s.code === sectorCode) ?? { friendly: 'NAICS Sector ' + sectorCode };
            return {
                rank: i + 1,
                code: item.naics_code,
                title: item.naics_title,
                count: item.count,
                sectorCode,
                sectorFriendly: sectorEntry.friendly // Used for top 10 all sectors, shows specific sector it belongs to
            };
        });
    }

    get selectedDigitLabel(): string {
        return this.digitLevels.find(l => l.agglevel === this.selectedAggLevel)?.friendly ?? '';
    }

    //To display format like "3 Digit Codes: Subsectors" -> "Subsectors" in the top 10 table header
    get selectedTrimmedLabel(): string {
        const label = this.selectedDigitLabel;
        if (!label) {
            return 'Industries';
        }
        return label.includes(':') ? label.split(':')[1].trim() : label;
    }

    getSectorColor(code: string): string {
        return NAICS_COLORS[code] ?? '#888888';
    }
}
